/**
 * OCP-1038 - the closed set of `ocp_audit_log.target_type` values. One list
 * for the api (AuditService, the `GET /v1/admin/audit` filter) and the admin
 * audit page; the `ocp_get_audit_log` MCP tool mirrors it and
 * apps/api/src/routes/admin/audit.test.ts fails when the two drift.
 *
 * The target itself is a UUID (`target_id`) or a text key (`target_key`: a
 * product slug, a provider product id, an OMS connection id), never both.
 */
export const AUDIT_TARGET_TYPES = [
  'tenant',
  'code',
  'waitlist',
  'vps',
  'user',
  'session',
  'system',
  // OCP-111 Customer-Portal auth events. Separate target type from
  // `user` (operator) so audit-log filters can disambiguate customer-
  // facing vs operator-facing actions without a second table.
  'portal_user',
  'portal_customer',
  // OCP-991 - discount codes: issued, revoked, redeemed.
  'discount_code',
  // OCP-1036 - company packages: offered, withdrawn, accepted.
  'company_package',
  // OCP-994 - an order whose payment event named a purged discount code.
  'portal_order',
  // OCP-116 License-Authority - operator-issued/revoked licenses.
  'portal_license',
  // OCP-598 tenant feedback intake - report received / ticket created /
  // reply synced / operator retry.
  'feedback_report',
  // OCP-528 Customer-side subscription lifecycle (cancel / reactivate).
  // The Terms restrict cancellation to an account administrator, so we
  // have to be able to prove WHICH portal user performed it - that
  // evidence is this target type plus the `actor_*` metadata.
  'portal_subscription',
  // OCP-149 OMS admin surface (admin.orboto.io/admin/oms/*). Separate
  // target types per OMS resource so audit-log filters can scope
  // operator queries to "all OMS customer mutations" vs "all OMS
  // suppression mutations" without parsing the `action` string.
  'oms_customer',
  'oms_api_key',
  // OCP-779 - the OMS-side connection behind an OAuth Mail grant.
  'oms_connection',
  // OCP-932 - one row of the payment-provider webhook inbox (operator re-drive).
  'provider_webhook_event',
  'oms_sender_domain',
  'oms_suppression',
  // OCP-200 Admin-side Self-host notify management.
  'selfhost_notification',
  // OCP-85 Drip-template + per-tenant drip actions.
  'drip_template',
  // OCP-298 large-tenant sales-gate approval requests.
  'provisioning_approval',
  // OCP-930 - a bot's request to run a person-level action, and its decision.
  'operator_approval',
  // OCP-238 MCP OAuth client management (list / delete / prune).
  'oauth_client',
  // OCP-485 operator-scheduled maintenance windows + per-tenant version pins.
  'maintenance_window',
  // OCP-492 affiliate commission ledger (created / voided / matured / clawback).
  'affiliate_commission',
  // OCP-383 refer-a-friend give-get reward ledger (created / voided / granted / refund_after_grant).
  'referral_reward',
  // OCP-519 managed-AI gateway runtime model list (add / update / delete).
  'ai_gateway_model',
  // OCP-497 account-scoped managed-AI API keys (create / revoke / block / unblock).
  'account_ai_key',
  // OCP-669 autonomous alert triage (ticket opened / acked / resolved / escalated).
  'operator_notification',
  // OCP-617 support inbox - a conversation (ticket filed, reply sent, poll requested).
  'support_thread',
  // OCP-1038 - a catalog product, keyed by its slug (`target_key`).
  'product',
] as const;

export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number];
